import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import './LocalDivisionsAndTeamsStep.scss';

const TEAM_LOGO_MAX_BYTES = 2 * 1024 * 1024;
const TEAM_LOGO_ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];

export interface LocalTeam {
  id: string;
  teamName: string;
  teamCode: string;
  description: string;
  logoBase64?: string;
  logoFileType?: string;
  logoFileName?: string;
  logoPreviewUrl?: string;
}

export interface LocalGroup {
  id: string;
  name: string;
  teams: LocalTeam[];
}

export interface LocalDivision {
  id: string;
  divisionName: string;
  groups: LocalGroup[];
  activeGroupId?: string;
}

interface Props {
  initial?: LocalDivision[];
  onBack: () => void;
  onNext: (divisions: LocalDivision[]) => void;
}

let _idCounter = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now()}-${_idCounter++}`;

const newTeam = (): LocalTeam => ({
  id: nextId('team'),
  teamName: '',
  teamCode: '',
  description: '',
});

const newGroup = (idx: number): LocalGroup => ({
  id: nextId('grp'),
  name: `Group ${idx + 1}`,
  teams: [],
});

const newDivision = (idx: number): LocalDivision => {
  const g = newGroup(0);
  return {
    id: nextId('div'),
    divisionName: `Division ${idx + 1}`,
    groups: [g],
    activeGroupId: g.id,
  };
};

function readTeamLogo(
  file: File,
): Promise<{ base64: string; fileType: string; previewUrl: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const commaIdx = result.indexOf(',');
      const base64 = commaIdx >= 0 ? result.slice(commaIdx + 1) : result;
      const ext =
        (file.type || '').split('/')[1]?.toLowerCase().split(';')[0] ||
        file.name.split('.').pop()?.toLowerCase() ||
        'png';
      resolve({ base64, fileType: ext, previewUrl: result });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const PlusCircle = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

export default function LocalDivisionsAndTeamsStep({ initial, onBack, onNext }: Props) {
  const logoInputsRef = useRef<Record<string, HTMLInputElement | null>>({});
  const [divisions, setDivisions] = useState<LocalDivision[]>(() => {
    if (initial && initial.length > 0) {
      return initial.map((d) => ({
        ...d,
        activeGroupId: d.activeGroupId ?? d.groups[0]?.id,
      }));
    }
    return [newDivision(0)];
  });
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    if (initial && initial.length > 0) map[initial[0].id] = true;
    return map;
  });
  const [confirmRemoveGroup, setConfirmRemoveGroup] = useState<{
    divisionId: string;
    groupId: string;
    teamCount: number;
  } | null>(null);

  // Expand the first division by default whenever the list changes from empty
  if (divisions.length > 0 && Object.values(expanded).every((v) => !v)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    setExpanded({ [divisions[0].id]: true });
  }

  const updateDivision = (id: string, patch: Partial<LocalDivision>) => {
    setDivisions((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  };

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const addDivision = () => {
    const fresh = newDivision(divisions.length);
    setDivisions((prev) => [...prev, fresh]);
    setExpanded((prev) => ({ ...prev, [fresh.id]: true }));
  };

  const removeDivision = (id: string) => {
    setDivisions((prev) => (prev.length <= 1 ? prev : prev.filter((d) => d.id !== id)));
  };

  const addGroup = (divisionId: string) => {
    setDivisions((prev) =>
      prev.map((d) => {
        if (d.id !== divisionId) return d;
        const fresh = newGroup(d.groups.length);
        return { ...d, groups: [...d.groups, fresh], activeGroupId: fresh.id };
      }),
    );
  };

  const switchActiveGroup = (divisionId: string, groupId: string) => {
    updateDivision(divisionId, { activeGroupId: groupId });
  };

  const requestRemoveGroup = (divisionId: string, groupId: string) => {
    const division = divisions.find((d) => d.id === divisionId);
    if (!division || division.groups.length <= 1) return;
    const group = division.groups.find((g) => g.id === groupId);
    if (!group) return;
    if (group.teams.length === 0) {
      doRemoveGroup(divisionId, groupId);
      return;
    }
    setConfirmRemoveGroup({ divisionId, groupId, teamCount: group.teams.length });
  };

  const doRemoveGroup = (divisionId: string, groupId: string) => {
    setDivisions((prev) =>
      prev.map((d) => {
        if (d.id !== divisionId) return d;
        const remaining = d.groups.filter((g) => g.id !== groupId);
        return {
          ...d,
          groups: remaining,
          activeGroupId: d.activeGroupId === groupId ? remaining[0]?.id : d.activeGroupId,
        };
      }),
    );
    setConfirmRemoveGroup(null);
  };

  const addTeam = (divisionId: string, groupId: string) => {
    setDivisions((prev) =>
      prev.map((d) =>
        d.id === divisionId
          ? {
              ...d,
              groups: d.groups.map((g) =>
                g.id === groupId ? { ...g, teams: [...g.teams, newTeam()] } : g,
              ),
            }
          : d,
      ),
    );
  };

  const updateTeam = (
    divisionId: string,
    groupId: string,
    teamId: string,
    patch: Partial<LocalTeam>,
  ) => {
    setDivisions((prev) =>
      prev.map((d) =>
        d.id === divisionId
          ? {
              ...d,
              groups: d.groups.map((g) =>
                g.id === groupId
                  ? { ...g, teams: g.teams.map((t) => (t.id === teamId ? { ...t, ...patch } : t)) }
                  : g,
              ),
            }
          : d,
      ),
    );
  };

  const removeTeam = (divisionId: string, groupId: string, teamId: string) => {
    setDivisions((prev) =>
      prev.map((d) =>
        d.id === divisionId
          ? {
              ...d,
              groups: d.groups.map((g) =>
                g.id === groupId ? { ...g, teams: g.teams.filter((t) => t.id !== teamId) } : g,
              ),
            }
          : d,
      ),
    );
  };

  const handleNext = () => {
    // Validate: every division/group needs a name; team rows with any data must have a name.
    for (const d of divisions) {
      if (!d.divisionName.trim()) {
        toast.error('Each division needs a name.');
        return;
      }
      if (d.groups.length === 0) {
        toast.error(`Division "${d.divisionName}" needs at least one group.`);
        return;
      }
      for (const g of d.groups) {
        if (!g.name.trim()) {
          toast.error(`Group in "${d.divisionName}" needs a name.`);
          return;
        }
        for (const t of g.teams) {
          const filled = t.teamName.trim() || t.teamCode.trim() || t.description.trim();
          if (filled && !t.teamName.trim()) {
            toast.error(`Group "${g.name}" has a team row missing a team name.`);
            return;
          }
        }
      }
    }
    const cleaned = divisions.map((d) => ({
      ...d,
      groups: d.groups.map((g) => ({
        ...g,
        teams: g.teams.filter((t) => t.teamName.trim().length > 0),
      })),
    }));
    onNext(cleaned);
  };

  return (
    <div className="create-league-step-form local-divisions-step">
      <h2 className="create-league-step-title">Divisions &amp; Teams</h2>

      {divisions.map((d) => {
        const isOpen = !!expanded[d.id];
        const activeGroup = d.groups.find((g) => g.id === d.activeGroupId) ?? d.groups[0];
        return (
          <section key={d.id} className={`ldt-division ${isOpen ? 'ldt-division--open' : ''}`}>
            <button
              type="button"
              className="ldt-division-header"
              onClick={() => toggleExpanded(d.id)}
              aria-expanded={isOpen}
            >
              <input
                className="ldt-division-name"
                value={d.divisionName}
                maxLength={60}
                onChange={(e) => updateDivision(d.id, { divisionName: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                placeholder="Division name"
              />
              <span className="ldt-division-tools" onClick={(e) => e.stopPropagation()}>
                {divisions.length > 1 && (
                  <button
                    type="button"
                    className="ldt-remove-division"
                    onClick={() => removeDivision(d.id)}
                    aria-label="Remove division"
                  >
                    <TrashIcon />
                  </button>
                )}
                <span className="ldt-chevron" aria-hidden>
                  <Chevron open={isOpen} />
                </span>
              </span>
            </button>

            {isOpen && activeGroup && (
              <div className="ldt-division-body">
                <div className="ldt-groups">
                  {d.groups.map((g) => {
                    const active = g.id === activeGroup.id;
                    return (
                      <div
                        key={g.id}
                        className={`ldt-group-chip ${active ? 'is-active' : ''} ${d.groups.length === 1 ? 'is-lone' : ''}`}
                        onClick={() => switchActiveGroup(d.id, g.id)}
                      >
                        <input
                          className="ldt-group-name"
                          value={g.name}
                          maxLength={40}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            setDivisions((prev) =>
                              prev.map((dd) =>
                                dd.id === d.id
                                  ? {
                                      ...dd,
                                      groups: dd.groups.map((gg) =>
                                        gg.id === g.id ? { ...gg, name: e.target.value } : gg,
                                      ),
                                    }
                                  : dd,
                              ),
                            )
                          }
                        />
                        {d.groups.length > 1 && (
                          <button
                            type="button"
                            className="ldt-group-x"
                            onClick={(e) => {
                              e.stopPropagation();
                              requestRemoveGroup(d.id, g.id);
                            }}
                            aria-label={`Remove ${g.name}`}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className="ldt-group-add"
                    onClick={() => addGroup(d.id)}
                    aria-label="Add group"
                  >
                    +
                  </button>
                </div>

                <div className="ldt-table-card">
                  <div className="ldt-table-card-header">Add Teams in Bulk</div>
                  <div className="ldt-table-wrap">
                    <table className="ldt-teams-table">
                      <thead>
                        <tr>
                          <th className="ldt-col-actions" aria-label="" />
                          <th className="ldt-col-num">#</th>
                          <th className="ldt-col-logo">Logo</th>
                          <th>Team Name</th>
                          <th>Team Code</th>
                          <th>Team Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeGroup.teams.length === 0 && (
                          <tr className="ldt-teams-empty-row">
                            <td colSpan={6}>No teams added yet — click "Add another row" below.</td>
                          </tr>
                        )}
                        {activeGroup.teams.map((t, idx) => (
                          <tr key={t.id}>
                            <td className="ldt-col-actions">
                              <button
                                type="button"
                                className="ldt-team-remove"
                                onClick={() => removeTeam(d.id, activeGroup.id, t.id)}
                                aria-label="Remove team"
                              >
                                <TrashIcon />
                              </button>
                            </td>
                            <td className="ldt-col-num">{idx + 1}</td>
                            <td className="ldt-col-logo">
                              <div className="ldt-logo-cell">
                                {t.logoPreviewUrl ? (
                                  <button
                                    type="button"
                                    className="ldt-logo-thumb"
                                    onClick={() => logoInputsRef.current[t.id]?.click()}
                                    aria-label="Replace logo"
                                  >
                                    <img src={t.logoPreviewUrl} alt="" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="ldt-logo-empty"
                                    onClick={() => logoInputsRef.current[t.id]?.click()}
                                    aria-label="Upload logo"
                                  >
                                    +
                                  </button>
                                )}
                                {t.logoPreviewUrl && (
                                  <button
                                    type="button"
                                    className="ldt-logo-clear"
                                    onClick={() =>
                                      updateTeam(d.id, activeGroup.id, t.id, {
                                        logoBase64: undefined,
                                        logoFileType: undefined,
                                        logoFileName: undefined,
                                        logoPreviewUrl: undefined,
                                      })
                                    }
                                    aria-label="Remove logo"
                                  >
                                    ×
                                  </button>
                                )}
                                <input
                                  ref={(el) => {
                                    logoInputsRef.current[t.id] = el;
                                  }}
                                  type="file"
                                  accept={TEAM_LOGO_ACCEPT.join(',')}
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (!TEAM_LOGO_ACCEPT.includes(file.type)) {
                                      toast.error('Logo must be a JPG, PNG, or WebP image.');
                                      e.target.value = '';
                                      return;
                                    }
                                    if (file.size > TEAM_LOGO_MAX_BYTES) {
                                      toast.error('Logo must be 2 MB or smaller.');
                                      e.target.value = '';
                                      return;
                                    }
                                    const { base64, fileType, previewUrl } = await readTeamLogo(file);
                                    updateTeam(d.id, activeGroup.id, t.id, {
                                      logoBase64: base64,
                                      logoFileType: fileType,
                                      logoFileName: file.name,
                                      logoPreviewUrl: previewUrl,
                                    });
                                    e.target.value = '';
                                  }}
                                />
                              </div>
                            </td>
                            <td>
                              <input
                                value={t.teamName}
                                maxLength={60}
                                placeholder="Team Name..."
                                onChange={(e) =>
                                  updateTeam(d.id, activeGroup.id, t.id, { teamName: e.target.value })
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={t.teamCode}
                                maxLength={6}
                                placeholder="Team Code..."
                                onChange={(e) =>
                                  updateTeam(d.id, activeGroup.id, t.id, {
                                    teamCode: e.target.value.toUpperCase(),
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={t.description}
                                maxLength={120}
                                placeholder="Description..."
                                onChange={(e) =>
                                  updateTeam(d.id, activeGroup.id, t.id, { description: e.target.value })
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className="ldt-add-row"
                    onClick={() => addTeam(d.id, activeGroup.id)}
                  >
                    <PlusCircle />
                    Add another row
                  </button>
                </div>
              </div>
            )}
          </section>
        );
      })}

      <button type="button" className="ldt-add-division" onClick={addDivision}>
        + Add Division
      </button>

      <div className="create-league-step-actions">
        <button type="button" className="create-league-btn-outline" onClick={onBack}>
          PREVIOUS
        </button>
        <button type="button" className="create-league-btn-primary" onClick={handleNext}>
          NEXT
        </button>
      </div>

      {confirmRemoveGroup && (
        <div className="ldt-confirm-overlay" onClick={() => setConfirmRemoveGroup(null)}>
          <div className="ldt-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p>
              Remove this group and its <strong>{confirmRemoveGroup.teamCount}</strong> team
              {confirmRemoveGroup.teamCount === 1 ? '' : 's'}?
            </p>
            <div className="ldt-confirm-actions">
              <button
                type="button"
                className="create-league-btn-outline"
                onClick={() => setConfirmRemoveGroup(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="create-league-btn-primary"
                onClick={() => doRemoveGroup(confirmRemoveGroup.divisionId, confirmRemoveGroup.groupId)}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
